import {
  HttpErrorResponse,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpParams,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import * as Sentry from '@sentry/angular';

import { ErrorHandlerService } from '@app-services/error-handler.service';

/**
 * Headers omitidos do curl gerado. São removidos por completo (não impressos como
 * `<redacted>`) para que a string do curl nunca contenha palavras como
 * "authorization", o que faria o data scrubber do Sentry substituir o campo
 * `curl` inteiro por `[Filtered]`.
 */
const OMITTED_HEADERS = new Set(['authorization', 'cookie', 'set-cookie', 'x-xsrf-token']);

/**
 * Deriva a tag `service` a partir do host da URL. Ex.:
 * `workoutservice-api.prd.g.actuar.cloud` -> `workoutservice-api`.
 * Como todos os serviços são monitorados, sempre retorna um valor.
 */
/**
 * Base exigida pelo construtor `URL` para conseguir parsear uma URL RELATIVA.
 * Não é um endereço de rede e nunca é acessado: só existe para o parser ter uma
 * origem válida quando recebe algo como `/Pessoas/Obter`. Por isso não vem da
 * configuração remota — trocá-lo por um valor configurável transformaria um
 * idioma de parsing em configuração de rede, sem nada do outro lado.
 */
const BASE_PARA_PARSE_DE_URL_RELATIVA = 'http://localhost';

function resolveServiceTag(url: string): string {
  try {
    const host = new URL(url, BASE_PARA_PARSE_DE_URL_RELATIVA).hostname;
    return host.split('.')[0] || host;
  } catch {
    return 'unknown';
  }
}

/**
 * Escapa um valor para uso dentro de uma string shell com aspas simples: cada
 * `'` vira `'\''` (fecha aspas, aspa escapada, reabre aspas).
 */
function shellEscape(value: string): string {
  return value.replace(/'/g, String.raw`'\''`);
}

function serializeBody(body: unknown): string | null {
  if (body == null) return null;
  if (body instanceof HttpParams) {
    return body.toString();
  }
  if (
    body instanceof FormData ||
    body instanceof Blob ||
    body instanceof ArrayBuffer
  ) {
    return '[binary body omitted]';
  }
  if (typeof body === 'string') return body;
  try {
    return JSON.stringify(body);
  } catch {
    return String(body);
  }
}

/**
 * Reconstrói um comando `curl` copiável para a requisição que falhou, de modo
 * que a chamada possa ser reproduzida no terminal. Headers sensíveis são
 * omitidos por completo.
 */
function buildCurl(request: HttpRequest<unknown>): string {
  const parts = [`curl -X ${request.method} '${request.urlWithParams}'`];

  for (const name of request.headers.keys()) {
    if (OMITTED_HEADERS.has(name.toLowerCase())) continue;
    const value = request.headers.getAll(name)?.join(', ') ?? '';
    parts.push(`-H '${name}: ${shellEscape(value)}'`);
  }

  const body = serializeBody(request.body);
  if (body != null) {
    parts.push(`--data '${shellEscape(body)}'`);
  }

  parts.push('--compressed');
  return parts.join(' \\\n  ');
}

/**
 * Normaliza o corpo da resposta de erro para o "Additional Data". Objetos são
 * serializados com indentação para que toda a estrutura aninhada fique visível
 * na issue (passar o objeto cru para `setExtra` deixaria o normalizador do
 * Sentry truncar níveis profundos). Strings são retornadas como estão; payloads
 * binários são resumidos.
 */
function serializeResponse(body: unknown): unknown {
  if (body == null) return null;
  if (typeof body === 'string') return body;
  if (body instanceof Blob || body instanceof ArrayBuffer) {
    return '[binary response omitted]';
  }
  try {
    return JSON.stringify(body, null, 2);
  } catch {
    return body;
  }
}

/**
 * Caminho do endpoint usado para agrupamento, com segmentos dinâmicos (GUIDs,
 * ids numéricos) e query string removidos, para que requisições ao mesmo
 * endpoint compartilhem uma issue enquanto endpoints distintos ficam separados.
 */
function fingerprintPath(url: string): string {
  try {
    const urlObj = new URL(url, BASE_PARA_PARSE_DE_URL_RELATIVA);
    return urlObj.pathname
      .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '/:id')
      .replace(/\/\d+/g, '/:id');
  } catch {
    return url;
  }
}

/**
 * Reporta apenas falhas de servidor (5xx).
 *
 * - `status === 0`: nenhuma resposta HTTP chegou ao browser (requisição
 *   abortada, sem conectividade ou bloqueio de CORS) — não carrega status
 *   acionável, é ruído.
 * - `4xx`: erros de cliente (validação, permissão, não encontrado) esperados na
 *   operação normal — não são reportados.
 */
function shouldReport(error: HttpErrorResponse): boolean {
  return error.status >= 500;
}

function reportToSentry(
  request: HttpRequest<unknown>,
  error: HttpErrorResponse,
  service: string,
  errorMessage: string | null,
): void {
  const statusCode = String(error.status);
  const curl = buildCurl(request);

  Sentry.withScope((scope) => {
    // Tags são filtráveis/pesquisáveis na UI do Sentry.
    scope.setTag('service', service);
    scope.setTag('status_code', statusCode);
    scope.setTag('http.method', request.method);

    // Agrupa em uma issue por serviço + método + endpoint + status + mensagem,
    // em vez de deixar o Sentry agrupar pela stack trace (sintética e idêntica),
    // o que fundiria todos os endpoints numa única issue.
    scope.setFingerprint([
      'sentry-http-error',
      service,
      request.method,
      fingerprintPath(request.url),
      statusCode,
      ...(errorMessage ? [errorMessage] : []),
    ]);

    scope.setContext('request', {
      method: request.method,
      url: request.urlWithParams,
      status: error.status,
      statusText: error.statusText,
      service,
    });

    // O curl e o corpo da resposta ficam no "Additional Data" (extra) para
    // serem lidos direto na issue.
    scope.setExtra('curl', curl);

    const response = serializeResponse(error.error);
    if (response != null) {
      scope.setExtra('response', response);
    }

    const title = `[${service}] HTTP ${error.status} on ${request.method} ${request.url}`;

    let exception: Error;
    if (error.error instanceof Error) {
      exception = error.error;
    } else {
      exception = new Error(errorMessage ? `${title} — ${errorMessage}` : title);
    }

    Sentry.captureException(exception);
  });
}

/**
 * Interceptor HTTP que reporta ao Sentry as requisições que falharam. Para cada
 * resposta de erro captura:
 *  - um `curl` reproduzível da requisição original (headers sensíveis omitidos),
 *    exposto em "Additional Data";
 *  - o corpo da resposta HTTP (objeto ou texto), também em "Additional Data";
 *  - tags `service`, `status_code` e `http.method` para filtrar as issues.
 *
 * O erro original é sempre re-lançado para que os handlers de erro das stores
 * continuem funcionando. O envio efetivo só ocorre em produção, pois o Sentry
 * só é inicializado nesse ambiente (fora dele, captureException é no-op).
 */
export const SentryErrorInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
): ReturnType<HttpHandlerFn> => {
  const errorHandlerService = inject(ErrorHandlerService);

  return next(request).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse && shouldReport(error)) {
        let errorMessage: string | null = null;
        try {
          errorMessage = errorHandlerService.getHttpErrorMessage(error);
        } catch {
          // Impede que um erro de parsing bloqueie o envio ao Sentry.
        }
        reportToSentry(request, error, resolveServiceTag(request.url), errorMessage);
      }
      return throwError(() => error);
    }),
  );
};
